/**
 * Social Platform Deep Link Utility
 *
 * Handles opening social media links on mobile devices by using native app deep links
 * with fallback to web URLs if the app isn't installed.
 *
 * Usage:
 *   import { openSocialLink } from '@/utils/socialDeepLinks';
 *   openSocialLink('https://instagram.com/devourplay');
 *
 * Deep-link templates in SOCIAL_PLATFORM_CONFIG support three substitution
 * variables:
 *   {handle}  — capture group from the platform's urlPattern, path-encoded
 *               (slashes preserved — see generateDeepLink for rationale)
 *   {path}    — alias of {handle}, used by templates that prefer path-style
 *               readability (YouTube)
 *   {fullUrl} — full input URL passed to openSocialLink, query-encoded.
 *               Used by templates that route via in-app browsers or need a
 *               browser_fallback_url (Facebook posts).
 */

import { FaInstagram, FaXTwitter, FaTiktok, FaYoutube, FaTwitch, FaDiscord, FaFacebook, FaSnapchat, FaLinkedin, FaGlobe, FaLink, FaThreads, FaReddit, FaSteam, FaBluesky, FaTelegram, FaSpotify } from "react-icons/fa6";
import { IconType } from "react-icons";
import { isMobile, isTablet, isIOS, isAndroid } from "react-device-detect";

export enum SocialPlatform {
    INSTAGRAM = "INSTAGRAM",
    INSTAGRAM_POST = "INSTAGRAM_POST",
    INSTAGRAM_REEL = "INSTAGRAM_REEL",
    TWITTER = "TWITTER",
    TWITTER_POST = "TWITTER_POST",
    TIKTOK = "TIKTOK",
    TIKTOK_VIDEO = "TIKTOK_VIDEO",
    YOUTUBE = "YOUTUBE",
    YOUTUBE_VIDEO = "YOUTUBE_VIDEO",
    YOUTUBE_SHORT = "YOUTUBE_SHORT",
    TWITCH = "TWITCH",
    KICK = "KICK",
    DISCORD = "DISCORD",
    FACEBOOK = "FACEBOOK",
    FACEBOOK_POST = "FACEBOOK_POST",
    SNAPCHAT = "SNAPCHAT",
    THREADS = "THREADS",
    LINKEDIN = "LINKEDIN",
    REDDIT = "REDDIT",
    REDDIT_POST = "REDDIT_POST",
    STEAM = "STEAM",
    BLUESKY = "BLUESKY",
    TELEGRAM = "TELEGRAM",
    SPOTIFY = "SPOTIFY",
    WEBSITE = "WEBSITE",
    OTHER = "OTHER",
}

export interface SocialPlatformConfig {
    name: string;
    icon: IconType;
    color: string;
    baseUrl?: string;
    deepLink?: {
        ios: string;
        android: string;
    };
    handlePlaceholder?: string;
    urlPattern?: RegExp;
}

// Kick doesn't have a react-icons icon, we'll use FaGlobe as fallback
const FaKick = FaGlobe;

export const SOCIAL_PLATFORM_CONFIG: Record<SocialPlatform, SocialPlatformConfig> = {
    /*
     * ============================================
     * INSTAGRAM
     * ============================================
     */
    [SocialPlatform.INSTAGRAM_POST]: {
        name: "Instagram Post",
        icon: FaInstagram,
        color: "#E4405F",
        deepLink: {
            ios: "instagram://media?id={handle}",
            android: "intent://instagram.com/p/{handle}/#Intent;package=com.instagram.android;scheme=https;end",
        },
        urlPattern: /(?:www\.)?instagram\.com\/p\/([^\/\?#]+)/i,
    },
    [SocialPlatform.INSTAGRAM_REEL]: {
        name: "Instagram Reel",
        icon: FaInstagram,
        color: "#E4405F",
        deepLink: {
            ios: "instagram://reels?id={handle}",
            android: "intent://instagram.com/reel/{handle}/#Intent;package=com.instagram.android;scheme=https;end",
        },
        urlPattern: /(?:www\.)?instagram\.com\/reel\/([^\/\?#]+)/i,
    },
    [SocialPlatform.INSTAGRAM]: {
        name: "Instagram",
        icon: FaInstagram,
        color: "#E4405F",
        baseUrl: "https://instagram.com/",
        deepLink: {
            ios: "instagram://user?username={handle}",
            android: "intent://instagram.com/_u/{handle}/#Intent;package=com.instagram.android;scheme=https;end",
        },
        handlePlaceholder: "@username",
        urlPattern: /(?:www\.)?instagram\.com\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * TWITTER / X
     * ============================================
     */
    [SocialPlatform.TWITTER_POST]: {
        name: "X Post",
        icon: FaXTwitter,
        color: "#FFFFFF",
        deepLink: {
            ios: "twitter://status?id={handle}",
            android: "intent://twitter.com/i/status/{handle}/#Intent;package=com.twitter.android;scheme=https;end",
        },
        urlPattern: /(?:www\.)?(?:twitter|x)\.com\/[^\/]+\/status\/(\d+)/i,
    },
    [SocialPlatform.TWITTER]: {
        name: "X (Twitter)",
        icon: FaXTwitter,
        color: "#FFFFFF",
        baseUrl: "https://x.com/",
        deepLink: {
            ios: "twitter://user?screen_name={handle}",
            android: "intent://twitter.com/{handle}/#Intent;package=com.twitter.android;scheme=https;end",
        },
        handlePlaceholder: "@username",
        // Matches: x.com/username, x.com/intent/follow?screen_name=username, twitter.com/username
        urlPattern: /(?:www\.)?(?:twitter|x)\.com\/(?:intent\/follow\?screen_name=|@?)?([^\/\?#&]+)/i,
    },

    /*
     * ============================================
     * TIKTOK
     * ============================================
     */
    [SocialPlatform.TIKTOK_VIDEO]: {
        name: "TikTok Video",
        icon: FaTiktok,
        color: "#FFFFFF",
        deepLink: {
            ios: "tiktok://video/{handle}",
            android: "intent://www.tiktok.com/video/{handle}/#Intent;package=com.zhiliaoapp.musically;scheme=https;end",
        },
        urlPattern: /(?:www\.)?tiktok\.com\/@[^\/]+\/video\/(\d+)/i,
    },
    [SocialPlatform.TIKTOK]: {
        name: "TikTok",
        icon: FaTiktok,
        color: "#FFFFFF",
        baseUrl: "https://tiktok.com/@",
        deepLink: {
            ios: "tiktok://user?username={handle}",
            android: "intent://www.tiktok.com/@{handle}/#Intent;package=com.zhiliaoapp.musically;scheme=https;end",
        },
        handlePlaceholder: "@username",
        urlPattern: /(?:www\.)?tiktok\.com\/@([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * YOUTUBE
     * ============================================
     */
    [SocialPlatform.YOUTUBE_VIDEO]: {
        name: "YouTube Video",
        icon: FaYoutube,
        color: "#FF0000",
        deepLink: {
            ios: "youtube://watch?v={handle}",
            android: "intent://www.youtube.com/watch?v={handle}/#Intent;package=com.google.android.youtube;scheme=https;end",
        },
        // Matches youtube.com/watch?v=VIDEO_ID and youtu.be/VIDEO_ID
        urlPattern: /(?:(?:www\.)?youtube\.com\/watch\?v=|youtu\.be\/)([^&\?#]+)/i,
    },
    [SocialPlatform.YOUTUBE_SHORT]: {
        name: "YouTube Short",
        icon: FaYoutube,
        color: "#FF0000",
        deepLink: {
            ios: "youtube://shorts/{handle}",
            android: "intent://www.youtube.com/shorts/{handle}/#Intent;package=com.google.android.youtube;scheme=https;end",
        },
        urlPattern: /(?:www\.)?youtube\.com\/shorts\/([^\/\?#]+)/i,
    },
    [SocialPlatform.YOUTUBE]: {
        name: "YouTube",
        icon: FaYoutube,
        color: "#FF0000",
        baseUrl: "https://youtube.com/",
        deepLink: {
            ios: "youtube://www.youtube.com/{path}",
            android: "intent://www.youtube.com/{path}/#Intent;package=com.google.android.youtube;scheme=https;end",
        },
        handlePlaceholder: "@channel or /c/channel",
        urlPattern: /(?:www\.)?youtube\.com\/(?:@|c\/|channel\/|user\/)?([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * TWITCH
     * ============================================
     */
    [SocialPlatform.TWITCH]: {
        name: "Twitch",
        icon: FaTwitch,
        color: "#9146FF",
        baseUrl: "https://twitch.tv/",
        deepLink: {
            ios: "twitch://stream/{handle}",
            android: "intent://www.twitch.tv/{handle}/#Intent;package=tv.twitch.android.app;scheme=https;end",
        },
        handlePlaceholder: "channel_name",
        urlPattern: /(?:www\.)?twitch\.tv\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * KICK
     * ============================================
     */
    [SocialPlatform.KICK]: {
        name: "Kick",
        icon: FaKick,
        color: "#53FC18",
        baseUrl: "https://kick.com/",
        deepLink: {
            // Kick doesn't have well-documented deep links yet - fallback to web
            ios: "https://kick.com/{handle}",
            android: "https://kick.com/{handle}",
        },
        handlePlaceholder: "channel_name",
        urlPattern: /(?:www\.)?kick\.com\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * DISCORD
     * ============================================
     */
    [SocialPlatform.DISCORD]: {
        name: "Discord",
        icon: FaDiscord,
        color: "#5865F2",
        baseUrl: "https://discord.gg/",
        deepLink: {
            ios: "discord://discord.gg/{handle}",
            android: "intent://discord.gg/{handle}/#Intent;package=com.discord;scheme=https;end",
        },
        handlePlaceholder: "invite_code",
        urlPattern: /(?:www\.)?discord\.(?:gg|com\/invite)\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * FACEBOOK
     * ============================================
     */
    [SocialPlatform.FACEBOOK_POST]: {
        name: "Facebook Post",
        icon: FaFacebook,
        color: "#1877F2",
        /*
         * Facebook deep-link strategy for posts:
         *
         *   iOS — `fb://facewebmodal/f?href=<encoded full URL>` opens the
         *   FB app's in-app browser to the URL. Works with the new
         *   pfbid... post URL format (the legacy `fb://post/<numeric>`
         *   only handles numeric story_fbid IDs). No "auto-like" URL
         *   scheme exists on iOS; the user still taps Like inside the
         *   FB app — this just maximizes the chance they land on the
         *   right post in the right app.
         *
         *   Android — intent:// URL with full host+path preserved (so
         *   pfbid pages resolve), package pinned to FB Katana, and
         *   S.browser_fallback_url so a user without the FB app still
         *   gets the post in their browser instead of a dead-end.
         *
         * Both templates substitute `{fullUrl}` from openSocialLink's
         * input URL — see generateDeepLink for the substitution logic.
         * The {handle} match group below captures `<page>/posts/<id>`
         * (rather than just <id>) so the Android intent host+path is
         * the same post the user actually wanted, not the page root.
         */
        deepLink: {
            ios: "fb://facewebmodal/f?href={fullUrl}",
            android: "intent://www.facebook.com/{handle}#Intent;scheme=https;package=com.facebook.katana;S.browser_fallback_url={fullUrl};end",
        },
        urlPattern: /facebook\.com\/([^\/]+\/posts\/[^\/\?#]+)/i,
    },
    [SocialPlatform.FACEBOOK]: {
        name: "Facebook",
        icon: FaFacebook,
        color: "#1877F2",
        baseUrl: "https://facebook.com/",
        deepLink: {
            ios: "fb://profile/{handle}",
            android: "intent://www.facebook.com/{handle}/#Intent;package=com.facebook.katana;scheme=https;end",
        },
        handlePlaceholder: "page_name or profile_id",
        urlPattern: /(?:www\.)?facebook\.com\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * SNAPCHAT
     * ============================================
     */
    [SocialPlatform.SNAPCHAT]: {
        name: "Snapchat",
        icon: FaSnapchat,
        color: "#FFFC00",
        baseUrl: "https://snapchat.com/add/",
        deepLink: {
            ios: "snapchat://add/{handle}",
            android: "intent://snapchat.com/add/{handle}/#Intent;package=com.snapchat.android;scheme=https;end",
        },
        handlePlaceholder: "username",
        urlPattern: /(?:www\.)?snapchat\.com\/add\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * THREADS
     * ============================================
     */
    [SocialPlatform.THREADS]: {
        name: "Threads",
        icon: FaThreads,
        color: "#FFFFFF",
        baseUrl: "https://threads.net/@",
        deepLink: {
            ios: "barcelona://user?username={handle}",
            android: "intent://www.threads.net/@{handle}/#Intent;package=com.instagram.barcelona;scheme=https;end",
        },
        handlePlaceholder: "@username",
        urlPattern: /(?:www\.)?threads\.net\/@([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * LINKEDIN
     * ============================================
     */
    [SocialPlatform.LINKEDIN]: {
        name: "LinkedIn",
        icon: FaLinkedin,
        color: "#0A66C2",
        baseUrl: "https://linkedin.com/in/",
        deepLink: {
            ios: "linkedin://profile/{handle}",
            android: "intent://www.linkedin.com/in/{handle}/#Intent;package=com.linkedin.android;scheme=https;end",
        },
        handlePlaceholder: "profile_name",
        urlPattern: /(?:www\.)?linkedin\.com\/(?:in|company)\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * REDDIT
     * ============================================
     */
    [SocialPlatform.REDDIT_POST]: {
        name: "Reddit Post",
        icon: FaReddit,
        color: "#FF4500",
        deepLink: {
            ios: "reddit://reddit.com/{handle}",
            android: "intent://reddit.com/{handle}/#Intent;package=com.reddit.frontpage;scheme=https;end",
        },
        // Matches /r/subreddit/comments/postid/
        urlPattern: /(?:www\.)?reddit\.com\/(r\/[^\/]+\/comments\/[^\/]+)/i,
    },
    [SocialPlatform.REDDIT]: {
        name: "Reddit",
        icon: FaReddit,
        color: "#FF4500",
        baseUrl: "https://reddit.com/r/",
        deepLink: {
            ios: "reddit://reddit.com/r/{handle}",
            android: "intent://reddit.com/r/{handle}/#Intent;package=com.reddit.frontpage;scheme=https;end",
        },
        handlePlaceholder: "subreddit_name",
        urlPattern: /(?:www\.)?reddit\.com\/r\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * STEAM
     * ============================================
     */
    [SocialPlatform.STEAM]: {
        name: "Steam",
        icon: FaSteam,
        color: "#1B2838",
        baseUrl: "https://store.steampowered.com/",
        deepLink: {
            ios: "steam://store/{handle}",
            android: "intent://store.steampowered.com/app/{handle}/#Intent;package=com.valvesoftware.android.steam.community;scheme=https;end",
        },
        handlePlaceholder: "app_id or profile",
        // Matches store pages and community profiles
        urlPattern: /(?:store\.)?steampowered\.com\/(?:app\/(\d+)|id\/([^\/\?#]+))/i,
    },

    /*
     * ============================================
     * BLUESKY
     * ============================================
     */
    [SocialPlatform.BLUESKY]: {
        name: "Bluesky",
        icon: FaBluesky,
        color: "#0085FF",
        baseUrl: "https://bsky.app/profile/",
        deepLink: {
            // Bluesky app deep links
            ios: "bluesky://profile/{handle}",
            android: "intent://bsky.app/profile/{handle}/#Intent;package=xyz.blueskyweb.app;scheme=https;end",
        },
        handlePlaceholder: "handle.bsky.social",
        urlPattern: /(?:www\.)?bsky\.app\/profile\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * TELEGRAM
     * ============================================
     */
    [SocialPlatform.TELEGRAM]: {
        name: "Telegram",
        icon: FaTelegram,
        color: "#26A5E4",
        baseUrl: "https://t.me/",
        deepLink: {
            ios: "tg://resolve?domain={handle}",
            android: "intent://t.me/{handle}/#Intent;package=org.telegram.messenger;scheme=https;end",
        },
        handlePlaceholder: "username or invite",
        urlPattern: /(?:www\.)?t\.me\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * SPOTIFY
     * ============================================
     */
    [SocialPlatform.SPOTIFY]: {
        name: "Spotify",
        icon: FaSpotify,
        color: "#1DB954",
        baseUrl: "https://open.spotify.com/",
        deepLink: {
            ios: "spotify://user/{handle}",
            android: "intent://open.spotify.com/user/{handle}/#Intent;package=com.spotify.music;scheme=https;end",
        },
        handlePlaceholder: "artist/playlist/user id",
        // Matches various Spotify URLs
        urlPattern: /(?:open\.)?spotify\.com\/(?:user|artist|playlist|album|track)\/([^\/\?#]+)/i,
    },

    /*
     * ============================================
     * GENERIC
     * ============================================
     */
    [SocialPlatform.WEBSITE]: {
        name: "Website",
        icon: FaGlobe,
        color: "#6B7280",
    },
    [SocialPlatform.OTHER]: {
        name: "Other",
        icon: FaLink,
        color: "#6B7280",
    },
};

/**
 * Detect the social platform from a URL
 */
export function detectPlatformFromUrl(url: string): { platform: SocialPlatform; handle?: string } {
    for (const [platform, config] of Object.entries(SOCIAL_PLATFORM_CONFIG)) {
        if (config.urlPattern) {
            const match = url.match(config.urlPattern);
            if (match) {
                return {
                    platform: platform as SocialPlatform,
                    handle: match[1],
                };
            }
        }
    }
    return { platform: SocialPlatform.WEBSITE };
}

/**
 * Check if the device is mobile or tablet
 * Uses react-device-detect for consistent, session-stable detection
 * Tablets are included since they typically have native apps installed
 */
export function isMobileDevice(): boolean {
    return isMobile || isTablet;
}

/**
 * Check if the device is a tablet
 * Uses react-device-detect for consistent, session-stable detection
 */
export function isTabletDevice(): boolean {
    return isTablet;
}

/**
 * Check if the device is iOS
 * Uses react-device-detect for consistent, session-stable detection
 */
export function isIOSDevice(): boolean {
    return isIOS;
}

/**
 * Check if the device is Android
 * Uses react-device-detect for consistent, session-stable detection
 */
export function isAndroidDevice(): boolean {
    return isAndroid;
}

/**
 * Generate a deep link URL for a given platform and handle.
 *
 * `fullUrl` is optional and only consulted when the deep-link template
 * references `{fullUrl}` — used for platforms (e.g. Facebook posts)
 * where the most reliable deep-link form is the full URL passed to an
 * in-app browser modal rather than a synthetic ID-based scheme.
 *
 * Encoding model:
 *   - {handle}/{path} are substituted via encodeURI so forward slashes
 *     are PRESERVED. This matters for FACEBOOK_POST whose capture is
 *     `<page>/posts/<id>` — encodeURIComponent would escape `/` to
 *     `%2F` and break Android intent host+path resolution (the FB app
 *     wouldn't open and the browser_fallback_url silently takes over,
 *     masking the failure). All urlPatterns in SOCIAL_PLATFORM_CONFIG
 *     already exclude `/`, `?`, `#` from the capture via character
 *     classes like `[^\/\?#]+`, so encodeURI is safe — only `%` and
 *     non-ASCII would need encoding and those don't appear in handles
 *     produced by the regex.
 *   - {fullUrl} is substituted via encodeURIComponent because it lives
 *     in query-value position (`?href=...`, `S.browser_fallback_url=...`)
 *     where reserved chars like `&`, `=`, `?`, `#` MUST be escaped or
 *     the surrounding URL/intent string will be misparsed.
 */
export function generateDeepLink(platform: SocialPlatform, handle: string, fullUrl?: string): string | null {
    const config = SOCIAL_PLATFORM_CONFIG[platform];
    if (!config.deepLink) return null;

    const template = isIOSDevice() ? config.deepLink.ios : config.deepLink.android;
    const encodedHandle = encodeURI(handle);
    let result = template.replace("{handle}", encodedHandle).replace("{path}", encodedHandle);
    if (fullUrl) {
        result = result.replace("{fullUrl}", encodeURIComponent(fullUrl));
    }
    return result;
}

/**
 * Open a social link with mobile deep link support
 *
 * On desktop: Opens in a new tab
 * On mobile: Tries to open the native app directly, falls back to web URL
 *
 * @param url - The full URL to open (e.g., "https://instagram.com/devourplay")
 * @param onFallback - Optional callback when falling back to web URL
 */
export function openSocialLink(url: string, onFallback?: () => void): void {
    // Block non-http(s) URLs before any branch executes (prevents javascript: / data: execution)
    if (!(/^https?:\/\//i).test(url)) {
        return;
    }

    // Desktop - just open in new tab
    if (!isMobileDevice()) {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
    }

    // Mobile - try deep link
    const { platform, handle } = detectPlatformFromUrl(url);
    /*
     * Pass `url` as fullUrl so deep-link templates that prefer the
     * complete URL (Facebook post facewebmodal, Android intent
     * S.browser_fallback_url) get it. Templates that don't reference
     * {fullUrl} ignore it harmlessly.
     */
    const deepLink = handle ? generateDeepLink(platform, handle, url) : null;

    if (!deepLink) {
        // No app deep link available — open web URL externally via _blank
        window.open(url, "_blank", "noopener,noreferrer");
        return;
    }

    /*
     * "_system" is the Cordova/InAppBrowser target that calls UIApplication.shared.open()
     * on iOS — bypasses the WKWebView entirely and opens the native app or Safari.
     * Works in OutSystems/Realm if cordova-plugin-inappbrowser is present (standard).
     *
     * Heuristic: if the page still has focus after 1500ms the native app didn't open
     * (either not installed or the plugin isn't present). Falls back to the web URL.
     * Not reliable on all iOS browsers — this is best-effort.
     */
    window.open(deepLink, "_system");

    setTimeout(() => {
        if (document.hasFocus()) {
            // Deep link didn't open the native app — fall back to web URL in standard tab
            window.open(url, "_blank", "noopener,noreferrer");
            onFallback?.();
        }
    }, 1500);
}

/**
 * Get the icon component for a platform
 */
export function getPlatformIcon(platform: SocialPlatform): IconType {
    return SOCIAL_PLATFORM_CONFIG[platform]?.icon || FaLink;
}

/**
 * Get the platform config
 */
export function getPlatformConfig(platform: SocialPlatform): SocialPlatformConfig {
    return SOCIAL_PLATFORM_CONFIG[platform] || SOCIAL_PLATFORM_CONFIG[SocialPlatform.OTHER];
}

/**
 * Get icon and color for a URL (auto-detect platform)
 */
export function getIconForUrl(url: string): { icon: IconType; color: string; platform: SocialPlatform } {
    const { platform } = detectPlatformFromUrl(url);
    const config = getPlatformConfig(platform);
    return {
        icon: config.icon,
        color: config.color,
        platform,
    };
}
